import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getProductStats } from "../services/api";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Package, 
  Euro,
  Hash,
  TrendingUp
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
};

const ProductDetailPage = () => {
  const { productName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await getProductStats(decodeURIComponent(productName));
        setData(res.data);
      } catch (err) {
        toast.error("Produit non trouvé");
        navigate("/reports");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [productName, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#26788A]"></div>
      </div>
    );
  }

  if (!data) return null;

  const chartData = data.monthly?.map(m => ({
    month: m.month_label,
    CA: m.revenue_ttc,
    Quantité: m.quantity
  })) || [];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{data.product.name}</h1>
          <p className="text-sm text-slate-500">Statistiques produit/service</p>
        </div>
      </div>

      {/* All-time KPIs */}
      <Card className="bg-gradient-to-r from-[#26788A] to-[#1D5E6C] text-white border-0">
        <CardContent className="p-6">
          <p className="text-sm text-white/70 mb-4">Depuis le début</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div>
              <p className="text-white/70 text-xs">CA total TTC</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(data.all_time.revenue_ttc)}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs">CA total HT</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(data.all_time.revenue_ht)}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Quantités vendues</p>
              <p className="text-2xl font-bold font-mono">{data.all_time.quantity_sold}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Prix moyen HT</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(data.all_time.avg_unit_price_ht)}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Factures</p>
              <p className="text-2xl font-bold font-mono">{data.all_time.invoice_count}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly chart - Revenue */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Euro size={16} /> CA par mois (12 derniers mois)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(0)}k` : val} />
                <Tooltip
                  formatter={(value, name) => name === "CA" ? formatCurrency(value) : value}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
                <Bar dataKey="CA" fill="#26788A" radius={[4, 4, 0, 0]} name="CA TTC" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly chart - Quantity */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Hash size={16} /> Quantités vendues par mois
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
                <Bar dataKey="Quantité" fill="#0891b2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Back to catalog */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          className="btn-outline"
          onClick={() => navigate("/catalog")}
        >
          <Package size={16} className="mr-2" />
          Voir le catalogue
        </Button>
      </div>
    </div>
  );
};

export default ProductDetailPage;
